import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
interface Cost {
  id: number;
  emoji: string;
  shortLabel: string;
  fullText: string;
}
const costs: Cost[] = [{
  id: 1,
  emoji: '🧶',
  shortLabel: 'Insumos',
  fullText: 'Material direto: linha, tecido, açúcar, embalagem do produto. **Quase todo mundo lembra desse.**'
}, {
  id: 2,
  emoji: '📦',
  shortLabel: 'Embalagem',
  fullText: 'Caixa, sacola, fita, etiqueta, cartão. Costuma sair entre **R$ 2 e R$ 8 por peça** — e é fácil esquecer.'
}, {
  id: 3,
  emoji: '⏱',
  shortLabel: 'Tempo',
  fullText: 'A sua hora de trabalho. Quem não cobra isso, **trabalha de graça** — só não percebe.'
}, {
  id: 4,
  emoji: '🏠',
  shortLabel: 'Aluguel',
  fullText: 'Aluguel do espaço, internet, conta de água. Se você produz em casa, **uma parte da sua casa virou seu ateliê** — e isso tem custo.'
}, {
  id: 5,
  emoji: '💳',
  shortLabel: 'Taxa de pagamento',
  fullText: 'Maquininha cobra 3% a 5%. Pix taxa cobra 0,5% a 1%. **R$ 100 vendidos viram R$ 95-97 no bolso.**'
}, {
  id: 6,
  emoji: '🛵',
  shortLabel: 'Entrega',
  fullText: 'Combustível, motoboy, taxa do iFood/Uber Flash. Se entrega é "cortesia", **quem paga é você** — direto da margem.'
}];
export function CostDiscoverySection() {
  // Ever-revealed: drives the counter + closing card
  const [revealed, setRevealed] = useState<Set<number>>(new Set());
  // Currently open: drives the expand/collapse
  const [openIds, setOpenIds] = useState<Set<number>>(new Set());
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, {
    once: true,
    margin: '-100px'
  });
  const handleToggle = (id: number) => {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    setRevealed(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };
  const allRevealed = revealed.size === costs.length;
  return <section ref={sectionRef} className="pt-16 md:pt-20 pb-6 md:pb-8 px-5 bg-white" data-id="element-96">
      <div className="max-w-2xl mx-auto pb-0" data-id="element-97">
        {/* Eyebrow */}
        <motion.p className="text-center text-rose font-caveat text-2xl md:text-5xl mb-4" initial={{
        opacity: 0,
        y: 10
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} data-id="element-98">
          antes de mais nada
        </motion.p>

        {/* Headline */}
        <motion.h2 className="text-center text-3xl md:text-4xl font-serif font-bold mb-4 leading-tight text-ink tracking-[-0.02em]" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.1,
        ease: 'easeOut'
      }} data-id="element-99">
          Quanto custa <span className="italic text-rose" data-id="element-100">de verdade</span> uma
          peça que você faz?
        </motion.h2>

        {/* Instrução */}
        <motion.p className="text-center text-ink-soft mb-10 max-w-md mx-auto text-[15px] md:text-base leading-relaxed" initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.6,
        delay: 0.3
      }} data-id="element-101">
          Toque pra descobrir cada custo que entra na conta. Você pode estar
          esquecendo a maioria.
        </motion.p>

        {/* Lista vertical de cards expansíveis */}
        <div className="flex flex-col gap-3" data-id="element-102">
          {costs.map((cost, idx) => <CostCard key={cost.id} cost={cost} isOpen={openIds.has(cost.id)} wasRevealed={revealed.has(cost.id)} onToggle={() => handleToggle(cost.id)} delay={0.4 + idx * 0.08} isInView={isInView} data-id="element-103" />)}
        </div>

        {/* Highlight CTA — Aurora calcula tudo isso */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 1.0,
        ease: 'easeOut'
      }} className="mt-10 md:mt-12 bg-rose text-white rounded-3xl p-6 md:p-10 shadow-card relative overflow-hidden" data-id="element-104">
          {/* Decorative blobs */}
          <div aria-hidden="true" className="absolute -top-12 -right-8 w-40 h-40 rounded-full bg-rose-deep/40 blur-2xl pointer-events-none" data-id="element-105" />
          <div aria-hidden="true" className="absolute -bottom-16 -left-10 w-44 h-44 rounded-full bg-gold/30 blur-3xl pointer-events-none" data-id="element-106" />

          <div className="relative flex flex-col items-center text-center" data-id="element-107">
            <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white text-[11px] md:text-xs font-bold tracking-[0.18em] uppercase px-3 py-1.5 rounded-full mb-4 border border-white/20" data-id="element-108">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" data-id="element-109" />
              A solução
            </span>

            <h3 className="font-serif font-bold text-2xl md:text-4xl leading-[1.1] tracking-[-0.02em] mb-2 max-w-xl" data-id="element-110">
              A Aurora calcula tudo isso pra você.
            </h3>
            <p className="font-caveat text-3xl md:text-4xl text-gold-soft" data-id="element-111">
              Em 30 segundos.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}
interface CostCardProps {
  cost: Cost;
  isOpen: boolean;
  wasRevealed: boolean;
  onToggle: () => void;
  delay: number;
  isInView: boolean;
}
function CostCard({
  cost,
  isOpen,
  wasRevealed,
  onToggle,
  delay,
  isInView
}: CostCardProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5,
    delay
  }} className={`w-full rounded-2xl shadow-md overflow-hidden border-2 transition-colors ${isOpen ? 'bg-rose-soft border-rose' : wasRevealed ? 'bg-white border-rose/30' : 'bg-white border-line'}`} data-id="element-112">
      <button onClick={onToggle} className="w-full flex items-center gap-3 md:gap-4 p-4 md:p-5 text-left min-h-[64px] active:scale-[0.99] transition-transform" aria-expanded={isOpen} aria-controls={`cost-${cost.id}-content`} data-id="element-113">
        <div className="text-3xl md:text-4xl flex-shrink-0" data-id="element-114">{cost.emoji}</div>
        <div className="flex-1 min-w-0" data-id="element-115">
          <div className="text-[15px] md:text-base font-semibold text-ink leading-tight" data-id="element-116">
            {cost.shortLabel}
          </div>
          {!wasRevealed && <div className="text-[12px] md:text-[13px] text-ink-soft mt-0.5" data-id="element-117">
              Toque para descobrir
            </div>}
        </div>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} className="flex-shrink-0 text-rose" data-id="element-118">
          <ChevronDown size={22} strokeWidth={2.5} data-id="element-119" />
        </motion.div>
      </button>

      <AnimatePresence initial={false} data-id="element-120">
        {isOpen && <motion.div id={`cost-${cost.id}-content`} initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="overflow-hidden" data-id="element-121">
            <div className="px-4 md:px-5 pb-4 md:pb-5" data-id="element-122">
              <div className="border-t border-rose/20 pt-3 md:pt-4" data-id="element-123">
                <p className="text-[14px] md:text-[15px] text-ink leading-relaxed" dangerouslySetInnerHTML={{
              __html: cost.fullText.replace(/\*\*(.*?)\*\*/g, '<strong class="text-rose-deep">$1</strong>')
            }} data-id="element-124" />
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}